/**
 * Razorpay Utility Functions
 *
 * Handles Razorpay Web Checkout integration for all Rebuilt payment flows:
 *   1. One-time orders (subscription ₹899, EMI repayments, etc.) — openRazorpayCheckout()
 *   2. UPI Autopay subscription mandate setup                       — openRazorpaySubscription()
 *
 * UPI Intent integration (v6.8 — May 2026)
 * ----------------------------------------
 * Per Razorpay support guidance, we now use config.display.blocks to surface
 * UPI as the primary payment method, with all three UPI flows enabled:
 *   - intent  (app handoff — GPay, PhonePe, Paytm icons on mobile web only)
 *   - collect (VPA entry box — name@upi)
 *   - qr      (scannable QR code)
 *
 * NOTE: UPI Intent (app icons) only renders inside a MOBILE web browser.
 *       On desktop browsers, Razorpay automatically shows only Collect + QR.
 *
 * We keep show_default_blocks: true so cards / netbanking continue to render
 * normally as fallbacks. This is a SAFER rollout that does not risk hiding
 * any working method.
 */

// ---------------------------------------------------------------------------
// Razorpay checkout script loader (idempotent — safe to call from anywhere)
// ---------------------------------------------------------------------------
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// ---------------------------------------------------------------------------
// Display config builder for UPI Intent (used by both one-time + subscription)
// ---------------------------------------------------------------------------
const buildUpiIntentDisplayBlocks = () => ({
  display: {
    blocks: {
      upi_intent: {
        name: 'Pay using UPI',
        instruments: [
          // App handoff (mobile web only — GPay/PhonePe/Paytm icons)
          { method: 'upi', flows: ['intent'] },
          // VPA entry — works on every platform
          { method: 'upi', flows: ['collect'] },
          // QR code — works on every platform (scan from another device)
          { method: 'upi', flows: ['qr'] },
        ],
      },
    },
    sequence: ['block.upi_intent'],
    preferences: {
      // SAFER ROLLOUT: keep Razorpay's default blocks visible BELOW our UPI
      // block. That way card/netbanking continues to work as a fallback even
      // if the UPI block has any rendering issue.
      show_default_blocks: true,
    },
  },
});

const buildUpiAutopayDisplayBlocks = () => ({
  display: {
    blocks: {
      upi_autopay: {
        name: 'UPI AutoPay',
        instruments: [
          { method: 'upi', flows: ['intent'] },
          { method: 'upi', flows: ['collect'] },
        ],
      },
    },
    sequence: ['block.upi_autopay'],
    preferences: {
      show_default_blocks: true,
    },
  },
});

// ---------------------------------------------------------------------------
// Resolve the Razorpay public key (prefer backend-provided, fall back to env)
// ---------------------------------------------------------------------------
const resolveKey = (provided) => {
  return (
    provided ||
    process.env.REACT_APP_RAZORPAY_KEY_ID ||
    ''
  );
};

// ---------------------------------------------------------------------------
// Open Razorpay Checkout for a ONE-TIME ORDER (subscription pay, EMI, etc.)
// ---------------------------------------------------------------------------
/**
 * @param {Object} opts - Payment options. Accepts BOTH the legacy snake_case
 *                        names (order_id, key_id, prefill_contact, prefill_email)
 *                        AND the new camelCase names (orderId, keyId, userMobile,
 *                        userEmail, userName) so existing callers keep working.
 */
export const openRazorpayCheckout = async (opts = {}) => {
  const scriptLoaded = await loadRazorpayScript();
  if (!scriptLoaded) {
    console.error('[Razorpay] Failed to load checkout script');
    if (opts.onFailure) opts.onFailure({ reason: 'Script load failed' });
    return;
  }

  // ---- Param normalisation (back-compat with legacy snake_case names) ----
  const orderId = opts.orderId || opts.order_id;
  const amount = opts.amount;
  const currency = opts.currency || 'INR';
  const keyId = resolveKey(opts.keyId || opts.key_id);
  const name = opts.name || 'Rebuilt';
  const description = opts.description || 'Rebuilt Payment';
  const userMobile = opts.userMobile || opts.prefill_contact || '';
  const userEmail = opts.userEmail || opts.prefill_email || (userMobile ? `${userMobile}@rebuiltindia.co` : '');
  const userName = opts.userName || opts.prefill_name || '';
  const onSuccess = opts.onSuccess;
  const onFailure = opts.onFailure;
  const onDismiss = opts.onDismiss;

  if (!keyId) {
    console.error('[Razorpay] Public key not configured (REACT_APP_RAZORPAY_KEY_ID missing and none passed by caller)');
    if (onFailure) onFailure({ reason: 'Razorpay key not configured' });
    return;
  }

  console.log('[Razorpay] Opening checkout', {
    key: keyId.slice(0, 15) + '…',
    orderId,
    amount,
  });

  const options = {
    key: keyId,
    amount,
    currency,
    name,
    description,
    order_id: orderId,

    // UPI Intent display blocks — surfaces UPI as the primary block at the top
    config: buildUpiIntentDisplayBlocks(),

    // Method allowlist — kept as a safety net alongside config.display.blocks.
    // Both can coexist: `method` controls which methods are enabled at all,
    // `config.display` controls the layout / ordering.
    method: {
      upi: true,
      card: true,
      netbanking: true,
      wallet: false,
      emi: false,
    },

    handler: function (response) {
      console.log('[Razorpay] Payment success:', response?.razorpay_payment_id);
      if (onSuccess) onSuccess(response);
    },

    prefill: {
      name: userName,
      email: userEmail,
      contact: userMobile,
    },

    notes: {
      platform: 'rebuilt_web',
    },

    theme: {
      color: '#1A3C6E',
    },

    modal: {
      confirm_close: true,
      ondismiss: function () {
        console.log('[Razorpay] Checkout dismissed by user');
        if (onDismiss) onDismiss();
        else if (onFailure) onFailure({ reason: 'Payment cancelled by user' });
      },
    },

    retry: {
      enabled: true,
      max_count: 3,
    },
  };

  try {
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      console.error('[Razorpay] Payment failed:', response?.error);
      if (onFailure) onFailure(response?.error || { reason: 'Payment failed' });
    });
    rzp.open();
  } catch (err) {
    console.error('[Razorpay] Error opening checkout:', err);
    if (onFailure) onFailure(err);
  }
};

// ---------------------------------------------------------------------------
// Open Razorpay Checkout for a SUBSCRIPTION (UPI Autopay mandate)
// ---------------------------------------------------------------------------
/**
 * Used during ContractMandate flow. Razorpay needs `subscription_id` (NOT
 * `order_id`) for mandate setup. The UPI Autopay display block surfaces UPI
 * Autopay options at the top while emandate falls back as default.
 */
export const openRazorpaySubscription = async (opts = {}) => {
  const scriptLoaded = await loadRazorpayScript();
  if (!scriptLoaded) {
    console.error('[Razorpay Subscription] Failed to load checkout script');
    if (opts.onFailure) opts.onFailure({ reason: 'Script load failed' });
    return;
  }

  const subscriptionId = opts.subscriptionId || opts.subscription_id;
  const keyId = resolveKey(opts.keyId || opts.razorpay_key || opts.key_id);
  const userMobile = opts.userMobile || opts.prefill_contact || '';
  const userEmail = opts.userEmail || opts.prefill_email || (userMobile ? `${userMobile}@rebuiltindia.co` : '');
  const userName = opts.userName || opts.prefill_name || '';
  const description = opts.description || 'EMI Auto-Pay Setup';

  if (!subscriptionId) {
    console.error('[Razorpay Subscription] subscription_id is required');
    if (opts.onFailure) opts.onFailure({ reason: 'subscription_id missing' });
    return;
  }
  if (!keyId) {
    console.error('[Razorpay Subscription] Public key not configured');
    if (opts.onFailure) opts.onFailure({ reason: 'Razorpay key not configured' });
    return;
  }

  console.log('[Razorpay Subscription] Opening mandate setup', {
    subscriptionId,
  });

  const options = {
    key: keyId,
    subscription_id: subscriptionId,
    name: 'Rebuilt',
    description,

    config: buildUpiAutopayDisplayBlocks(),

    // For mandates we enable upi + emandate as fallback. Cards/netbanking
    // mandates require additional NACH setup so we leave them disabled to
    // keep the UX clean.
    method: {
      upi: true,
      emandate: true,
      card: false,
      netbanking: false,
      wallet: false,
    },

    handler: function (response) {
      console.log('[Razorpay Subscription] Setup success:', response);
      if (opts.onSuccess) opts.onSuccess(response);
    },

    prefill: {
      name: userName,
      email: userEmail,
      contact: userMobile,
    },

    notes: {
      platform: 'rebuilt_web',
      flow: 'upi_autopay_mandate',
    },

    theme: {
      color: '#1A3C6E',
    },

    modal: {
      confirm_close: true,
      ondismiss: function () {
        console.log('[Razorpay Subscription] Mandate setup dismissed');
        if (opts.onDismiss) opts.onDismiss();
      },
    },
  };

  try {
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      console.error('[Razorpay Subscription] Failed:', response?.error);
      if (opts.onFailure) opts.onFailure(response?.error || { reason: 'Mandate setup failed' });
    });
    rzp.open();
  } catch (err) {
    console.error('[Razorpay Subscription] Error opening checkout:', err);
    if (opts.onFailure) opts.onFailure(err);
  }
};

// ---------------------------------------------------------------------------
// Poll payment status (used after one-time order to wait for webhook update)
// ---------------------------------------------------------------------------
export const pollPaymentStatus = async (checkStatusFn, maxAttempts = 40, interval = 3000) => {
  let attempts = 0;
  return new Promise((resolve, reject) => {
    const poll = setInterval(async () => {
      attempts++;
      try {
        const status = await checkStatusFn();
        if (status === 'success') {
          clearInterval(poll);
          resolve('success');
        } else if (status === 'failed') {
          clearInterval(poll);
          resolve('failed');
        } else if (attempts >= maxAttempts) {
          clearInterval(poll);
          resolve('timeout');
        }
      } catch (error) {
        console.error('[Razorpay] Error polling status:', error);
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          reject(error);
        }
      }
    }, interval);
  });
};
